import type { DecisionAgentDomain } from '../decision-agent-platform';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/cost-to-serve/
// cost-to-serve.types.ts (COST_TO_SERVE_DATA_LAKE_CONTEXT_KIND,
// COST_TO_SERVE_DATA_LAKE_SCHEMA_VERSION) — the exact pair
// cost-to-serve.data-lake-source.ts queries.
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): 2 APPROVED,
// ingest-anchored rows, latest approvedAt 2026-07-29, asOf 2025-12-31. This is
// the cleanest module in the batch: the freshest artifact's own evidenceRefs
// carry EXACTLY the eight evidence classes the manifest declares in
// requiredState — an 8-for-8 match, every one gapStatus=available. Nothing below
// is inferred.
//
// FRESHNESS NOTE (disclosed, #4449 lane 1): the live asOf is 2025-12-31, ~220
// days old. Seven of these keys carry a `monthly` (45-day) ceiling and one
// (customer_price_elasticity) a `quarterly` (135-day) ceiling, so under the
// readiness instrument this module reads STALE — an honest AMBER, not a green.
// Registering the contract is still the right move: it converts an unmeasurable
// CONTRACT_ABSENT into a measured, actionable staleness finding, which is the
// difference between "we cannot see it" and "it needs a refresh." Unlike
// carrier, cost-to-serve exposes asOf at the top level, so the instrument sees
// the real age and reports it correctly.
export const COST_TO_SERVE_EVIDENCE_KEYS = {
  accountRevenueSnapshot: 'approved_account_revenue_snapshot',
  accountCohortSnapshot: 'account_cohort_snapshot',
  accountRevenueConcentrationSnapshot: 'account_revenue_concentration_snapshot',
  activityCostDriverMap: 'activity_cost_driver_map',
  serviceTierCostTable: 'service_tier_cost_table',
  customerPriceElasticity: 'customer_price_elasticity',
  chartOfAccountsMapping: 'approved_chart_of_accounts_mapping',
  financeOperatingIncomeTieOut: 'approved_finance_operating_income_tie_out',
} as const;

type CostToServeEvidenceKey =
  (typeof COST_TO_SERVE_EVIDENCE_KEYS)[keyof typeof COST_TO_SERVE_EVIDENCE_KEYS];

export const COST_TO_SERVE_CONTEXT_KINDS = {
  governedSnapshot: 'decision_agent:cost_to_serve:governed_snapshot',
} as const;

export const COST_TO_SERVE_SCHEMA_VERSIONS = {
  governedSnapshot: 'cost_to_serve_governed_snapshot_v1',
} as const;

const NO_COST_TO_SERVE_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

// Mirrors cost-to-serve.manifest.ts requiredState exactly, key for key.
const COST_TO_SERVE_DOMAIN_BY_EVIDENCE_KEY: Readonly<
  Record<CostToServeEvidenceKey, DecisionAgentDomain>
> = {
  approved_account_revenue_snapshot: 'commercial',
  account_cohort_snapshot: 'commercial',
  account_revenue_concentration_snapshot: 'commercial',
  activity_cost_driver_map: 'operations',
  service_tier_cost_table: 'operations',
  customer_price_elasticity: 'commercial',
  approved_chart_of_accounts_mapping: 'finance',
  approved_finance_operating_income_tie_out: 'finance',
};

const COST_TO_SERVE_FULL_REQUIRED_FOR = [
  'monitoring',
  'scenario',
  'decision_pack',
  'activation',
  'ask_helix',
] as const;

const COST_TO_SERVE_ELASTICITY_REQUIRED_FOR = [
  'scenario',
  'decision_pack',
  'activation',
  'ask_helix',
] as const;

const costToServeEntry = (evidenceKey: CostToServeEvidenceKey, description: string) => {
  const isElasticity =
    evidenceKey === COST_TO_SERVE_EVIDENCE_KEYS.customerPriceElasticity;
  return defineModuleEvidenceEntry(evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.connectorDerived,
    contextKind: COST_TO_SERVE_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: COST_TO_SERVE_SCHEMA_VERSIONS.governedSnapshot,
    bindingPlaceholders: NO_COST_TO_SERVE_BINDING_PLACEHOLDERS,
    domain: COST_TO_SERVE_DOMAIN_BY_EVIDENCE_KEY[evidenceKey],
    freshnessCadence: isElasticity ? 'quarterly' : 'monthly',
    requiredFor: isElasticity
      ? COST_TO_SERVE_ELASTICITY_REQUIRED_FOR
      : COST_TO_SERVE_FULL_REQUIRED_FOR,
    missingBehavior: 'block',
    description,
  });
};

export const COST_TO_SERVE_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'cost-to-serve',
  {
    account_revenue_snapshot: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.accountRevenueSnapshot,
      'Approved account-level net revenue, volume, and service-tier evidence keyed to canonical ' +
        'customerId/accountId; the revenue base every cost-to-serve margin read divides against.',
    ),
    account_cohort_snapshot: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.accountCohortSnapshot,
      'Approved account-cohort evidence carried by the same governed snapshot; groups accounts ' +
        'so cost-to-serve differences are reported by cohort rather than per account.',
    ),
    account_revenue_concentration_snapshot: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.accountRevenueConcentrationSnapshot,
      'Approved account revenue-concentration evidence carried by the same governed snapshot; ' +
        'backs concentration and profitability-cliff reads.',
    ),
    activity_cost_driver_map: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.activityCostDriverMap,
      'Approved activity cost-driver map carried by the same governed snapshot; the allocation ' +
        'basis that turns activity volumes into served cost.',
    ),
    service_tier_cost_table: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.serviceTierCostTable,
      'Approved service-tier cost table carried by the same governed snapshot; prices each ' +
        'service tier so tier-mix changes can be costed.',
    ),
    customer_price_elasticity: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.customerPriceElasticity,
      'Approved customer price-elasticity evidence carried by the same governed snapshot; bounds ' +
        'the volume response assumed by any repricing or tier-migration scenario.',
    ),
    chart_of_accounts_mapping: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.chartOfAccountsMapping,
      'Approved chart-of-accounts mapping carried by the same governed snapshot; binds served ' +
        'cost to the governed account taxonomy.',
    ),
    finance_operating_income_tie_out: costToServeEntry(
      COST_TO_SERVE_EVIDENCE_KEYS.financeOperatingIncomeTieOut,
      'Approved operating-income tie-out carried by the same governed snapshot; reconciles ' +
        'cost-to-serve totals back to the governed P&L so the module cannot drift from finance.',
    ),
  },
  {
    evidenceKeys: Object.values(COST_TO_SERVE_EVIDENCE_KEYS),
    sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.connectorDerived],
    contextKinds: Object.values(COST_TO_SERVE_CONTEXT_KINDS),
    schemaVersions: Object.values(COST_TO_SERVE_SCHEMA_VERSIONS),
    stateKeys: Object.values(COST_TO_SERVE_EVIDENCE_KEYS),
    bindingPlaceholders: NO_COST_TO_SERVE_BINDING_PLACEHOLDERS,
  },
);

export type CostToServeModuleEvidenceContract = typeof COST_TO_SERVE_MODULE_EVIDENCE_CONTRACT;
